import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { apiCheckDomains } from "@/lib/namable";
import { Layers, Loader2, Copy, Check, Download, Lock, Zap } from "lucide-react";
import { toast } from "sonner";

const EXTENSIONS = [".com", ".net", ".org", ".io", ".ai", ".co"];
const MAX = 200;

export default function Bulk() {
  const navigate = useNavigate();
  const { usage } = useAuth();
  const { openPaywall } = useModals();
  const [text, setText] = useState("");
  const [exts, setExts] = useState([".com"]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isPaid = usage?.is_paid;

  const names = useMemo(
    () =>
      text
        .split(/[\n,]/)
        .map((n) => n.trim().toLowerCase().replace(/[^a-z0-9-]/g, ""))
        .filter(Boolean),
    [text]
  );
  const domains = useMemo(() => {
    const list = [];
    names.forEach((n) => exts.forEach((e) => list.push(n + e)));
    return Array.from(new Set(list)).slice(0, MAX);
  }, [names, exts]);

  const toggleExt = (e) => setExts((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));

  const run = async () => {
    if (domains.length === 0) {
      toast.error("Add some names and pick at least one extension.");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const data = await apiCheckDomains(domains);
      const rows = Object.entries(data.results || {}).map(([domain, available]) => ({ domain, available }));
      rows.sort((a, b) => (b.available === true) - (a.available === true));
      setResults(rows);
    } catch (e) {
      if (e?.response?.status === 402) openPaywall("bulk_locked");
      else toast.error("Bulk check failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const available = (results || []).filter((r) => r.available === true);

  const copyAvailable = () => {
    navigator.clipboard?.writeText(available.map((r) => r.domain).join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
    toast.success(`${available.length} available domains copied`);
  };

  const downloadCsv = () => {
    const csv = "domain,available\n" + (results || []).map((r) => `${r.domain},${r.available === true ? "yes" : r.available === false ? "no" : "unknown"}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "namable-bulk-check.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (usage && !isPaid) {
    return (
      <div className="max-w-[560px] mx-auto px-4 sm:px-6 py-24 text-center">
        <Seo title="Bulk domain check — Namable" description="Check hundreds of domains at once." path="/bulk" />
        <div className="w-14 h-14 rounded-[16px] bg-brand-soft text-brand flex items-center justify-center mx-auto">
          <Layers size={26} />
        </div>
        <h1 className="font-display text-[24px] font-extrabold text-ink mt-5">Bulk domain check</h1>
        <p className="text-ink-muted text-[15px] mt-2">Check up to {MAX} domains at once across every extension — a Pro & Starter feature.</p>
        <Button
          className="mt-6 h-12 px-6 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
          onClick={() => openPaywall("bulk_locked")}
          data-testid="bulk-upgrade-btn"
        >
          <Lock size={16} className="mr-2" /> Unlock bulk check
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Seo title="Bulk domain check — Namable" description="Check hundreds of domains at once across every extension." path="/bulk" />
      <p className="overline text-brand">Pro tools</p>
      <h1 className="font-display text-[30px] font-extrabold tracking-[-0.02em] text-ink mt-1.5">Bulk domain check</h1>
      <p className="text-ink-muted text-[14px] mt-1.5">Paste names (one per line), pick extensions, and check up to {MAX} domains at once.</p>

      <div className="card-soft p-5 sm:p-6 mt-7">
        <label className="text-[13px] font-semibold text-ink">Names</label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="bulk-names-input"
          placeholder={"novara\nvyntro\nkorva\nnexora"}
          className="mt-2.5 min-h-[150px] rounded-xl text-[15px] resize-none border-[color:var(--hairline-strong)] focus-visible:ring-brand font-mono"
        />

        <label className="text-[13px] font-semibold text-ink block mt-5">Extensions</label>
        <div className="flex flex-wrap gap-2 mt-2.5">
          {EXTENSIONS.map((e) => (
            <button
              key={e}
              onClick={() => toggleExt(e)}
              data-testid={`bulk-ext-${e.replace(".", "")}`}
              className={`px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-all ${
                exts.includes(e)
                  ? "bg-brand text-white border-brand shadow-glow-sm"
                  : "bg-surface text-ink-muted border-[color:var(--hairline-strong)] hover:border-brand/40"
              }`}
            >
              {e}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mt-6">
          <span className="text-[12px] text-ink-subtle font-semibold">
            {domains.length} / {MAX} domains
          </span>
          <Button
            onClick={run}
            disabled={loading || domains.length === 0}
            data-testid="bulk-check-btn"
            className="h-11 px-6 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <><Zap size={15} className="mr-2" /> Check domains</>}
          </Button>
        </div>
      </div>

      {results && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[14px] font-bold text-ink">
              <span className="text-success">{available.length}</span> available · {results.length} checked
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyAvailable} disabled={available.length === 0} className="h-9 rounded-lg font-semibold text-[13px]" data-testid="bulk-copy-available">
                {copied ? <Check size={14} className="mr-1.5 text-success" /> : <Copy size={14} className="mr-1.5" />} Copy available
              </Button>
              <Button variant="outline" onClick={downloadCsv} className="h-9 rounded-lg font-semibold text-[13px]" data-testid="bulk-download-csv">
                <Download size={14} className="mr-1.5" /> CSV
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {results.map((r) => (
              <div
                key={r.domain}
                className={`flex items-center justify-between rounded-lg border hairline px-4 py-3 ${r.available ? "bg-surface" : "bg-canvas-alt opacity-70"}`}
                data-testid={`bulk-result-${r.domain}`}
              >
                <span className="text-[14px] font-semibold text-ink truncate">{r.domain}</span>
                <AvailabilityBadge available={r.available} extension={r.domain.slice(r.domain.indexOf("."))} size="sm" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
